/**
 * Select Your Style
 *
 * The row of style tiles above a collection grid (`blocks/style-selector.liquid`). Each tile links
 * to a filtered URL such as `?filter.p.m.custom.style.halo=Halo`. When that URL is for this page's
 * own grid (`<collection-filters>`), a click toggles the grid's filter in place instead:
 *
 * - the filter is added or removed through the grid, so it shows with the other active filters,
 *   lands in the URL, and the grid's Reset clears it;
 * - a tile whose value has no products in the current results is disabled. The counts come from
 *   the grid after each load (`collection-filters:updated`), so they already take into account a
 *   picked diamond on Design Your Own and any other filters. A style that is on stays clickable,
 *   so it can be turned off. When every tile is disabled the whole row is hidden;
 * - the filter the tiles control is left out of the grid's desktop filter bar, which would
 *   otherwise show it as a second control once a tile is on. It stays in "All filters".
 *
 * Without a matching grid on the page (another collection, or the grid not loaded yet) the tiles
 * stay plain links.
 *
 * @module style-selector
 */

class StyleSelector extends HTMLElement {

  connectedCallback() {

    this.onUpdated = (event) => this.sync(event.target, event.detail);
    this.onPopState = () => this.sync(this.grid(), null);

    // The grid can be replaced (Design Your Own swaps it when a diamond is picked), so listen on
    // the document rather than on one grid.
    document.addEventListener('collection-filters:updated', this.onUpdated);
    window.addEventListener('popstate', this.onPopState);
    this.addEventListener('click', this.onClick.bind(this));

    this.sync(this.grid(), null);
  }

  disconnectedCallback() {
    document.removeEventListener('collection-filters:updated', this.onUpdated);
    window.removeEventListener('popstate', this.onPopState);
  }

  /**
   * @returns {?HTMLElement} The grid on this page
   */
  grid() {
    return document.querySelector('collection-filters');
  }

  /**
   * The filter a tile stands for, from its link.
   *
   * @param {HTMLAnchorElement} link
   * @returns {?{path: string, reference: string, value: string}}
   */
  tileFilter(link) {

    if (!link.dataset.filterHref) {
      link.dataset.filterHref = link.getAttribute('href') || '';
    }

    const url = new URL(link.dataset.filterHref, window.location.origin);
    const entry = [...url.searchParams.entries()].find(([key]) => key.startsWith('filter.p.m.'));

    return entry ? { path: url.pathname, reference: entry[0], value: entry[1] } : null;
  }

  links() {
    return [...this.querySelectorAll('a.style-icon-item__link')];
  }

  /**
   * @param {MouseEvent} event
   */
  onClick(event) {

    const link = event.target.closest('a.style-icon-item__link');

    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    if (link.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    const grid = this.grid();
    const filter = this.tileFilter(link);

    // Another page's filter, or no grid to apply it to: follow the link.
    if (!grid || !grid.activeFilters || !filter || filter.path !== window.location.pathname) return;

    event.preventDefault();

    const active = grid.activeFilters.some(item => item.referenceId === filter.reference);

    if (active) {
      grid.removeFilter(filter.reference);
      return;
    }

    grid.action('filter', filter.reference);
    grid.applyFilter(filter.reference, grid.reconstructFilterObjectFromReference(filter.reference, filter.value), filter.value);
  }

  /**
   * Mark the active tile and disable styles the current results don't have.
   *
   * @param {?HTMLElement} grid
   * @param {?{filters: Array, activeFilters: Array}} detail - From the grid's last load; null keeps
   *   the last known counts (back/forward before the grid reloads)
   */
  sync(grid, detail) {

    if (detail) {
      this.available = this.availableValues(detail.filters);
      this.removeInlineFacets(grid);
    }

    // The grid's filters once it exists; until then (or on a page whose grid is another
    // collection) the ones in the URL.
    const active = new Set(grid && grid.activeFilters
      ? grid.activeFilters.map(item => item.referenceId)
      : [...new URLSearchParams(window.location.search).keys()].filter(key => key.startsWith('filter.p.m.')));

    let usable = 0;

    this.links().forEach(link => {

      const filter = this.tileFilter(link);

      if (!filter) return;

      const isActive = active.has(filter.reference);
      const values = this.available && this.available.get(this.filterId(filter.reference));
      const available = !this.available || (values ? values.has(filter.value.toLowerCase()) : false);
      const disabled = !isActive && !available;

      link.classList.toggle('style-icon-item__link--active', isActive);
      link.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      link.classList.toggle('style-icon-item__link--disabled', disabled);

      if (disabled) {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      } else {
        usable++;
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      }
    });

    // Nothing to pick in these results (e.g. no setting for the picked diamond has one of these
    // styles): hide the row rather than show it all greyed out.
    this.hidden = usable === 0 && this.links().some(link => this.tileFilter(link));
  }

  /**
   * Drop the desktop dropdowns of the filters these tiles control (the grid recreates them on each
   * load, so this runs after every one). The "All filters" drawer keeps them.
   *
   * @param {?HTMLElement} grid
   */
  removeInlineFacets(grid) {

    if (!grid) return;

    // Only tiles that filter this page's own grid; links to another collection leave it alone.
    const ids = new Set(this.links()
      .map(link => this.tileFilter(link))
      .filter(filter => filter && filter.path === window.location.pathname)
      .map(filter => this.filterId(filter.reference)));

    ids.forEach(id => {
      const facet = grid.querySelector(`[id="facet_inline_${id}"]`);

      if (facet) facet.remove();
    });
  }

  /**
   * The grid filter a tile's reference belongs to: `filter.p.m.custom.style.halo` → `filter.p.m.custom.style`.
   *
   * @param {string} reference
   * @returns {string}
   */
  filterId(reference) {
    return reference.split('.').slice(0, 5).join('.');
  }

  /**
   * Values with products in the current results, lower-cased, by filter id. A filter missing
   * from the results has no products for any of its values.
   *
   * @param {Array} filters - The grid's filters (Typesense or Storefront shape)
   * @returns {Map<string, Set<string>>}
   */
  availableValues(filters) {

    return new Map((filters || []).map(filter => [
      filter.id,
      new Set((filter.values || []).filter(value => value.count > 0).map(value => String(value.label).toLowerCase()))
    ]));
  }
}

if (!customElements.get('style-selector')) {
  customElements.define('style-selector', StyleSelector);
}
